import { translate } from '../../Internationalization';
import React from 'react';

/**
 * Registry to preserve the external iframe across React lifecycle flickers
 * and navigation events. This avoids re-loading the script/iframe constantly.
 */
/** @type {HTMLIFrameElement | null} */
let cachedPortalIframe = null;
let cachedPortalPropertyId = null;
/** @type {'idle' | 'loading' | 'loaded'} */
let portalScriptState = 'idle';
const conciergeSelfPortalUrl = process.env.REACT_APP_CONCIERGE_SELF_PORTAL_URL;

/**
 * @typedef {Readonly<{
 *   address?: string | null,
 *   propertyId?: string | null,
 *   withTitle?: boolean,
 * }>} Props
 */

/** @extends {React.Component<Props, {}>} */
export default class MoveConciergePortal extends React.Component {
	/**
	 * @returns {void}
	 */
	componentDidMount() {
		this.#initializePortal();
	}

	/**
	 * @param {Props} prevProps
	 * @returns {void}
	 */
	componentDidUpdate(prevProps) {
		if (
			prevProps.address !== this.props.address ||
			prevProps.propertyId !== this.props.propertyId
		) {
			MoveConciergePortal.#resetPortal();
			this.#initializePortal();
		}
	}

	/**
	 * @returns {void}
	 */
	componentWillUnmount() {
		/**
		 * before React destroys the container. This keeps the iframe "alive"
		 * and prevents the "disappear" issue on return.
		 */
		const container = document.getElementById('mc-embed');
		if (!container) {
			return;
		}

		// Find the iframe injected by the external script
		const iframe = container.querySelector('iframe');
		if (iframe) {
			cachedPortalIframe = iframe;
			cachedPortalPropertyId = this.props.propertyId ?? null;
			// Detach from the disappearing container and stash in body
			cachedPortalIframe.style.display = 'none';
			document.body.appendChild(cachedPortalIframe);
		}
	}

	/**
	 * Initializes the portal by either restoring a cached iframe
	 * or injecting the script for the first time.
	 * @returns {void}
	 */
	#initializePortal() {
		const container = document.getElementById('mc-embed');
		if (!container) {
			return;
		}

		if (this.#restoreCachedPortal(container)) {
			return;
		}

		this.#injectPortalIfNeeded();
	}

	/**
	 * @returns {void}
	 */
	static #clearCachedPortal() {
		if (cachedPortalIframe) {
			cachedPortalIframe.remove();
		}

		cachedPortalIframe = null;
		cachedPortalPropertyId = null;
		portalScriptState = 'idle';
	}

	/**
	 * @returns {void}
	 */
	static #resetPortal() {
		const container = document.getElementById('mc-embed');
		const iframe = container?.querySelector('iframe');
		const existingScript = document.getElementById(
			'mooveguru-embed-script'
		);

		iframe?.remove();
		existingScript?.remove();
		MoveConciergePortal.#clearCachedPortal();
	}

	/**
	 * @param {HTMLElement} container
	 * @returns {boolean}
	 */
	#restoreCachedPortal(container) {
		if (
			cachedPortalIframe &&
			cachedPortalPropertyId !== (this.props.propertyId ?? null)
		) {
			MoveConciergePortal.#clearCachedPortal();
		}

		if (!cachedPortalIframe) {
			return false;
		}

		container.appendChild(cachedPortalIframe);
		cachedPortalIframe.style.display = 'block';
		return true;
	}

	/**
	 * @returns {void}
	 */
	#injectPortalIfNeeded() {
		if (portalScriptState === 'loaded' || portalScriptState === 'idle') {
			this.#injectScript();
		}
	}

	/**
	 * @returns {void}
	 * @throws {Error}
	 */
	// eslint-disable-next-line class-methods-use-this
	#injectScript() {
		if (!conciergeSelfPortalUrl) {
			throw new Error(
				'REACT_APP_CONCIERGE_SELF_PORTAL_URL is undefined.'
			);
		}

		const existingScript = document.getElementById(
			'mooveguru-embed-script'
		);
		if (existingScript) {
			existingScript.remove();
		}

		const tenant = MoveConciergePortal.#getTenant();
		const script = document.createElement('script');

		portalScriptState = 'loading';
		script.id = 'mooveguru-embed-script';
		script.src = conciergeSelfPortalUrl;
		script.setAttribute('data-company', tenant);
		script.setAttribute('data-embed-path', 'homekeepr');
		script.setAttribute('address', this.props.address ?? '');
		script.onload = () => {
			portalScriptState = 'loaded';
		};
		script.onerror = () => {
			portalScriptState = 'idle';
			script.remove();
		};

		document.body.appendChild(script);
	}

	/**
	 * @returns {string}
	 */
	static #getTenant() {
		const host = window.location.hostname.toLowerCase();
		if (host.includes('simplyhome')) {
			if (host.includes('era')) {
				return 'shera';
			}

			return 'sh';
		}

		return 'hk';
	}

	/**
	 * @returns {JSX.Element}
	 */
	render() {
		return (
			<main className="move-concierge-portal">
				{this.props.withTitle !== false ? (
					<header className="mt-10">
						<h2 className="hl-ms-6 mb-4">
							{translate('homeowner.nav.main.concierge')}
						</h2>
					</header>
				) : null}
				<div data-rendered={this.props !== null} id="mc-embed" />
			</main>
		);
	}
}
